'use client';

import { useState, type ReactElement, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { ShopperProducts } from '@/scapi';
import { Button } from '@/components/ui/button';
import { isProductSet } from '@/lib/product-utils';
import { useProductView } from '@/providers/product-view';
import { useTranslation } from 'react-i18next';
import { SvgCartArrow } from '../icons-eataly';
import ProductQuantityPicker from '../product-quantity-picker-custom';
import { UITarget } from '@/targets/ui-target';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Typography } from '../typography';
import { Spinner } from '../spinner';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';

interface ProductCartActionsProps {
    product: ShopperProducts.schemas['Product'];
    /** Called immediately before cart action starts (add or update) - useful for optimistic UI like closing modal */
    onBeforeCartAction?: () => void;
    /** Called after successful cart operation completes (add or update) */
    onCartSuccess?: () => void;
    /** Called if cart operation fails (add or update) */
    onCartError?: (error: unknown) => void;
    /** Called immediately before add to wishlist action starts */
    onBeforeAddToWishlist?: () => void;
    /** Called after successful add to wishlist action completes */
    onAddToWishlistSuccess?: () => void;
    /** Called if add to wishlist operation fails */
    onAddToWishlistError?: (error: unknown) => void;
    /**
     * When provided in add mode, renders a compact two-button layout:
     * "Add to Cart" + "Buy it Now" side by side. Express payments, BNPL,
     * wishlist, and share buttons are hidden in this layout.
     * Typically navigates to the PDP for the full purchase flow.
     */
    onBuyNow?: () => void;
}

type CartButtonState = {
    label: ReactNode;
    showIcon: boolean;
};

/**
 * Resolves add-to-cart button label and trailing icon visibility by mode,
 * loading state, and viewport. Mobile and large breakpoints use a spinner
 * while loading; medium and xlarge keep the "Adding to cart" copy.
 */
function getCartButtonState({
    isEditMode,
    isAddingToOrUpdatingCart,
    isMobile,
    isLarge,
    t,
}: {
    isEditMode: boolean;
    isAddingToOrUpdatingCart: boolean;
    isMobile: boolean;
    isLarge: boolean;
    t: TFunction<'product'>;
}): CartButtonState {
    if (isEditMode) {
        return { label: t('updateCart'), showIcon: true };
    }

    if (isAddingToOrUpdatingCart) {
        const showLoadingSpinner = isMobile || isLarge;

        return {
            label: showLoadingSpinner ? <Spinner size="sm" /> : t('addingToCart'),
            showIcon: !isMobile,
        };
    }

    return {
        label: isMobile ? t('addToCartMobile') : t('addToCart'),
        showIcon: true,
    };
}

function ProductCartActionsCustomEnabled({
    product,
    onBeforeCartAction,
    onCartSuccess,
    onCartError,
}: ProductCartActionsProps): ReactElement {
    const { t } = useTranslation('product');
    const { t: tCommon } = useTranslation('common');

    const isProductASet = isProductSet(product);
    // Bundles (multipack + assortment) ATC as the parent id; only product sets skip parent ATC.
    const showParentAddToCart = !isProductASet;
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isLarge = useMediaQuery('(min-width: 1024px) and (max-width: 1280px)');
    const {
        mode,
        isAddingToOrUpdatingCart,
        canAddToCart,
        handleAddToCart,
        handleUpdateCart,
        quantity,
        isOrderable,
        stockLevel,
        maxQuantity,
        setQuantity,
    } = useProductView();

    const isEditMode = mode === 'edit';

    // Local input string so inventory warnings can read the current typed value
    // while the parent renders them outside the picker row at full width.
    const [quantityInputValue, setQuantityInputValue] = useState<string>(quantity.toString());

    const onAddOrUpdateToCart = async () => {
        // Keep edit-mode optimistic close behavior, but for add-mode quick-add we
        // wait for success so the mounted hook can emit toast + open mini-cart.
        if (isEditMode) {
            onBeforeCartAction?.();
        }

        try {
            // Use handleUpdateCart in edit mode, handleAddToCart in add mode
            if (isEditMode) {
                await handleUpdateCart();
            } else {
                await handleAddToCart();
            }
            // Call success callback after API completes
            onCartSuccess?.();
        } catch (error) {
            onCartError?.(error);
        }
    };

    const getInventoryMessage = () => {
        if (!isOrderable) {
            return t('outOfStock', { productName: product.name || tCommon('product') });
        }

        const currentQuantity = parseInt(quantityInputValue, 10) || 0;
        if (stockLevel !== undefined && stockLevel > 0 && currentQuantity >= stockLevel) {
            return t('maxQuantityReached');
        }

        return null;
    };

    const inventoryMessage = getInventoryMessage();
    const effectiveMaxQuantity = maxQuantity ?? (stockLevel > 0 ? stockLevel : undefined);

    const cartButton = getCartButtonState({
        isEditMode,
        isAddingToOrUpdatingCart,
        isMobile,
        isLarge,
        t,
    });

    return (
        <div className="space-y-4">
            {/* Action Buttons */}
            <div className="flex w-full min-w-0 gap-4">
                <ProductQuantityPicker
                    inputValue={quantityInputValue}
                    onInputValueChange={setQuantityInputValue}
                    onQuantityChange={setQuantity}
                    maxQuantity={effectiveMaxQuantity}
                    disabled={!isOrderable}
                    className="shrink-0"
                />
                <UITarget targetId="sfcc.pdp.tax.productMessage" />

                {/* Action Buttons */}
                {/* Standard layout: single Add to Cart / Update button (includes bundles) */}
                {showParentAddToCart && (
                    <Button
                        data-testid="add-to-cart"
                        onClick={() => void onAddOrUpdateToCart()}
                        disabled={!canAddToCart || isAddingToOrUpdatingCart}
                        hierarchy="primary"
                        iconAfter={cartButton.showIcon ? <SvgCartArrow /> : undefined}
                        className="min-w-0 flex-1"
                        size="lg">
                        {cartButton.label}
                    </Button>
                )}
            </div>
            {/* Inventory message */}
            {inventoryMessage && (
                <Typography variant="small" className="text-destructive font-medium" role="alert" aria-live="polite">
                    {inventoryMessage}
                </Typography>
            )}
        </div>
    );
}

export default function ProductCartActionsCustom(props: ProductCartActionsProps): ReactElement | null {
    const commerceEnabled = useCommerceEnabled();

    return commerceEnabled ? <ProductCartActionsCustomEnabled {...props} /> : null;
}
